import React, { useEffect } from "react";
// Optional: reuse your existing stylesheet
// import "../../assets/scss/PrivacyPolicy.scss";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <div className="privacy-policy-main">
        <div className="privacy-policy-container">
          <div className="container">
            <div className="text-center">
              <h1>Privacy Policy</h1>
              <p><strong>Last Updated:</strong> Aug 25th 2025</p>
            </div>

            <section className="privacy-policy-section">
              <h2 className="section-title">1. Introduction</h2>
              <p>
                LoveHappiness (“we,” “us,” or “our”) operates the LoveHappiness mobile application (the “App” or “Service”). This Privacy Policy explains how we collect, store, use, and share personal data about you when you use our Service, in compliance with UK GDPR and the Data Protection Act 2018. By using the Service, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">2. Data Controller and Contact Details</h2>
              <ul>
                <li><strong>Data Controller:</strong> LoveHappiness</li>
                <li><strong>Registered Address:</strong> 36 Broad Lane, Hale, Cheshire, WA15 0DH</li>
                <li><strong>Company Number:</strong> 13803732</li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">3. What Data We Collect</h2>
              <p>We may collect the following categories of personal data:</p>
              <ul>
                <li><strong>Identity and contact data:</strong> name, username, email address, phone number, date of birth, gender, profile photos.</li>
                <li><strong>Account data:</strong> login details, passwords (securely hashed), device identifiers.</li>
                <li><strong>Usage data:</strong> how you use the Service, features used, time and date of interactions, clickstream data.</li>
                <li><strong>Content you provide:</strong> messages, photos, videos, bios, wellness prompts, and other profile content.</li>
                <li><strong>Video/audio data:</strong> data generated during video calls (e.g., duration, timestamps, call metadata). Unless you consent to recording or we provide optional recording features, we do not store or process raw video or audio content beyond metadata.</li>
                <li><strong>Technical data:</strong> IP address, browser type, operating system, mobile carrier, language, time zone, and crash logs.</li>
                <li><strong>Location data:</strong> approximate location derived from IP or user-supplied location fields (if applicable).</li>
                <li><strong>Health and wellbeing data:</strong> optional self-reported wellbeing prompts or preferences (only if you provide them).</li>
                <li><strong>Cookies and tracking:</strong> information collected via cookies and similar technologies (see our Cookies Policy).</li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">4. How We Use Your Data</h2>
              <ul>
                <li>To provide and operate the Service (account creation, matching, video intros, chat features).</li>
                <li>To communicate with you (service updates, security notices, account recovery).</li>
                <li>To personalise your experience (recommendations, filters, and preferences).</li>
                <li>To improve and develop the Service (analytics, troubleshooting).</li>
                <li>To ensure safety and security (verification steps, anti catfishing measures, abuse reporting).</li>
                <li>For legal compliance and enforcement (as required by law or to protect our rights).</li>
                <li>For marketing and communications (only if you have opted in; see Your Rights and marketing section).</li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">5. Legal Basis for Processing (UK GDPR)</h2>
              <ul>
                <li><strong>Contract:</strong> using the Service and performing our obligations under Terms of Use.</li>
                <li><strong>Legitimate Interests:</strong> safety, integrity, fraud prevention, and improving user experience.</li>
                <li><strong>Consent:</strong> for direct marketing or optional features (clearly opt-in).</li>
                <li><strong>Legal Obligation:</strong> to comply with applicable laws.</li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">6. Sharing Your Data</h2>
              <ul>
                <li><strong>Service providers:</strong> hosting, analytics, payment processing, customer support, and security services.</li>
                <li><strong>Safety and compliance partners:</strong> verification services, abuse prevention partners, and law enforcement as required by law.</li>
                <li><strong>Business transfers:</strong> in the event of a merger, acquisition, or sale of assets, your data may be transferred with appropriate protections.</li>
                <li><strong>Third-country transfers:</strong> if you are outside the UK or if data is stored outside the UK, we rely on legal safeguards (e.g., UK IDTA, SCCs) and ensure adequate protection for such transfers.</li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">7. International Data Transfers</h2>
              <p>
                Your data may be stored and processed in countries outside the UK. Where this happens, we implement appropriate safeguards to protect your data in line with UK GDPR requirements.
              </p>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">8. Data Retention</h2>
              <p>
                We retain your personal data for as long as necessary to provide the Service, fulfill the purposes described in this policy, and meet legal obligations. Typical retention periods may be: active account data for the duration of the relationship plus a defined period (e.g., [X] years) for legal, security, and data integrity purposes. Specific retention periods are documented in our Data Retention Schedule.
              </p>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">9. Your Rights under UK GDPR</h2>
              <p>You have rights regarding your personal data, including:</p>
              <ul>
                <li><strong>Right of access:</strong> you can request a copy of your data.</li>
                <li><strong>Right to rectification:</strong> you can correct inaccuracies.</li>
                <li><strong>Right to erasure:</strong> also known as the right to be forgotten, with limitations.</li>
                <li><strong>Right to restriction of processing.</strong></li>
                <li><strong>Right to data portability:</strong> receive your data in a structured, commonly used format.</li>
                <li><strong>Right to object:</strong> to processing based on legitimate interests or for direct marketing.</li>
                <li><strong>Rights related to automated decision-making and profiling (if applicable):</strong> details in our profiling policy.</li>
                <li>
                  <strong>Right to withdraw consent at any time</strong> (where consent is the legal basis). To exercise these rights, contact us at <a href="mailto:info@lovehappiness.co.uk">info@lovehappiness.co.uk</a>. We will respond in line with UK GDPR timelines (typically one month, extendable in complex cases).
                </li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">10. Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar technologies to operate the Service, analyse usage, and personalise content. You can manage cookie preferences in your browser or via in-app settings, where available. For full details, see our Cookies Policy.
              </p>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">11. Security</h2>
              <p>
                We implement technical and organisational measures to protect your data against unauthorized access, alteration, disclosure, or destruction. These include access controls, encryption, secure coding practices, and regular security assessments. However, no method of transmission over the internet or electronic storage is completely secure; we strive to reduce risk.
              </p>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">12. Children’s Privacy</h2>
              <p>
                The Service is not intended for children under 18. We do not knowingly collect data from children under 18. If we become aware that a child under 18 has provided personal data, we will take steps to delete it.
              </p>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">13. Marketing Communications</h2>
              <p>
                We may send you marketing communications only if you have opted in. You can opt out at any time via the unsubscribe link or by contacting us at <a href="mailto:info@lovehappiness.co.uk">info@lovehappiness.co.uk</a>.
              </p>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">14. Your Choices</h2>
              <ul>
                <li><strong>Account settings:</strong> update profile information, privacy preferences, and notification preferences.</li>
                <li><strong>Data portability:</strong> export data where available.</li>
                <li><strong>Deletion:</strong> request deletion of your account and personal data subject to applicable legal obligations.</li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">15. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will post the new policy on our website and update the Effective Date. If changes are significant, we may provide additional notice.
              </p>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">16. Contact Information</h2>
              <p>If you have questions about this Privacy Policy or our data practices, contact us at:</p>
              <ul>
                <li><strong>Email:</strong> <a href="mailto:info@lovehappiness.co.uk">info@lovehappiness.co.uk</a></li>
                <li><strong>Postal address:</strong> 36 Broad Lane, Hale, Cheshire WA15 0DH</li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">17. Additional Notices</h2>
              <ul>
                <li><strong>Data Processing Addendum:</strong> If you engage with third-party processors, a DPA may be available or attached as an addendum.</li>
                <li><strong>Privacy Policy link:</strong> A clear link to this Privacy Policy should be provided within the app and website.</li>
                <li><strong>ICO and complaints:</strong> You have the right to lodge a complaint with the Information Commissioner’s Office (ICO) in the UK if you believe your data protection rights have been violated.</li>
              </ul>
            </section>

            <section className="privacy-policy-section">
              <h2 className="section-title">Definitions</h2>
              <ul>
                <li><strong>Personal Data:</strong> any information relating to an identified or identifiable natural person.</li>
                <li><strong>Processing:</strong> any operation performed on personal data, including collection, storage, use, and disclosure.</li>
                <li><strong>Data Controller:</strong> the entity that determines purposes and means of processing personal data.</li>
              </ul>
            </section>

            {/*
            Notes for customisation (do not display publicly):
            • Replace placeholders with your actual company details.
            • If you use third-party analytics or marketing platforms, list them in a separate section of processors.
            • Include a data retention schedule with exact timeframes for different data types.
            • Have a UK-qualified solicitor review to ensure full UK compliance.
          */}
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
